
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { MOVIE_DATA } from './data/movies';
import { Movie } from './types';
import MovieCard from './components/MovieCard';
import { geminiService } from './services/geminiService';

const DECADES = ['All', '1930s', '1940s', '1950s', '1960s', '1970s', '1980s', '1990s', '2000s', '2010s', '2020s'];

const ALL_GENRES = [
  'Horror', 'Thriller', 'Drama', 'Action', 'Crime', 'Comedy', 'Sci-Fi',
  'Fantasy', 'Mystery', 'Animation', 'Documentary', 'Romance', 'War',
  'Musical', 'Giallo', 'Exploitation', 'Samurai', 'Yakuza', 'Adventure', 'Uncategorized'
];

const PACKS = ["Shin's", 'P2P', 'Exploitation', "Mexicano", "Pinkueiga", "Pacho", "Nomad", "Terror", "Derniers", 'Anime'];

const HERO_FRAMES = [
  'https://cdn.stillslab.com/movies/star-wars-the-mandalorian-and-grogu_frames/star-wars-the-mandalorian-and-grogu_frame_357.webp',
  'https://cdn.stillslab.com/movies/django-unchained_frames/django_unchained_frame_1738665940.527168.webp',
  'https://cdn.stillslab.com/movies/train-dreams_frames/train-dreams_frame_379.webp',
  'https://cdn.stillslab.com/movies/the-shining_frames/the_shining_frame_1738635065.027231.webp',
  'https://cdn.stillslab.com/movies/metropolis_frames/metropolis_frame_107.webp',
  'https://cdn.stillslab.com/movies/sinners_frames/sinners_frame_437.webp',
  'https://cdn.stillslab.com/movies/my-neighbor-totoro_frames/my-neighbor-totoro_frame_258.webp',
  'https://cdn.stillslab.com/movies/the-truman-show_frames/the-truman-show_frame_16.webp',
  'https://cdn.stillslab.com/movies/nightcrawler_frames/nightcrawler_frame_1738861481.198624.webp',
  'https://cdn.stillslab.com/movies/star-wars-the-mandalorian-and-grogu_frames/star-wars-the-mandalorian-and-grogu_frame_h5778.webp',
  'https://cdn.stillslab.com/movies/passenger_frames/passenger_frame_h92912.webp',
  'https://cdn.stillslab.com/movies/barry-lyndon_frames/barry-lyndon_frame_245.webp',
  'https://cdn.stillslab.com/movies/fallen-leaves_frames/fallen-leaves_frame_121.webp',
  'https://cdn.stillslab.com/movies/the-death-of-robin-hood_frames/the-death-of-robin-hood_frame_35.webp',
  'https://cdn.stillslab.com/movies/disclosure-day_frames/disclosure-day_frame_394.webp',
  'https://cdn.stillslab.com/movies/moonlight_frames/moonlight_frame_376.webp',
  'https://cdn.stillslab.com/movies/the-ring_frames/the-ring_frame_374.webp',
  'https://cdn.stillslab.com/movies/fear-and-loathing-in-las-vegas_frames/fear-and-loathing-in-las-vegas_frame_23.webp',
  'https://cdn.stillslab.com/movies/killhouse_frames/killhouse_frame_h1779.webp',
  'https://cdn.stillslab.com/movies/backrooms_frames/backrooms_frame_344.webp'
];
const THEMES = [
  {
    key: 'light',
    label: 'Light',
    colors: {
      bg: '#f4f7f1',
      bgElevated: '#f9fbf6',
      headerBg: 'rgba(244, 247, 241, 0.85)',
      panel: 'rgba(15, 23, 42, 0.04)',
      panelStrong: 'rgba(15, 23, 42, 0.08)',
      panelSoft: 'rgba(15, 23, 42, 0.03)',
      border: 'rgba(15, 23, 42, 0.1)',
      borderStrong: 'rgba(15, 23, 42, 0.16)',
      text: '#0f172a',
      textMuted: 'rgba(15, 23, 42, 0.65)',
      textSubtle: 'rgba(15, 23, 42, 0.5)',
      textFaint: 'rgba(15, 23, 42, 0.35)',
      textGhost: 'rgba(15, 23, 42, 0.2)',
      accent: '#16a34a',
      accentHover: '#22c55e',
      accentSoft: 'rgba(34, 197, 94, 0.15)',
      accentRing: 'rgba(34, 197, 94, 0.35)',
      accentShadow: 'rgba(34, 197, 94, 0.35)',
      accentContrast: '#f8fafc',
      glassBg: 'rgba(15, 23, 42, 0.04)',
      glassBorder: 'rgba(15, 23, 42, 0.1)',
      posterGradient: 'linear-gradient(to top, rgba(10, 10, 11, 0.75) 0%, rgba(10, 10, 11, 0.35) 40%, rgba(10, 10, 11, 0) 100%)'
    }
  },
  {
    key: 'dark',
    label: 'Dark',
    colors: {
      bg: '#0a0a0b',
      bgElevated: '#0f0f12',
      headerBg: 'rgba(10, 10, 11, 0.85)',
      panel: 'rgba(255, 255, 255, 0.05)',
      panelStrong: 'rgba(255, 255, 255, 0.09)',
      panelSoft: 'rgba(255, 255, 255, 0.03)',
      border: 'rgba(255, 255, 255, 0.08)',
      borderStrong: 'rgba(255, 255, 255, 0.16)',
      text: '#f4f4f5',
      textMuted: 'rgba(244, 244, 245, 0.6)',
      textSubtle: 'rgba(244, 244, 245, 0.45)',
      textFaint: 'rgba(244, 244, 245, 0.3)',
      textGhost: 'rgba(244, 244, 245, 0.18)',
      accent: '#f97316',
      accentHover: '#fb923c',
      accentSoft: 'rgba(249, 115, 22, 0.16)',
      accentRing: 'rgba(249, 115, 22, 0.35)',
      accentShadow: 'rgba(249, 115, 22, 0.35)',
      accentContrast: '#0b0b0b',
      glassBg: 'rgba(24, 24, 27, 0.6)',
      glassBorder: 'rgba(255, 255, 255, 0.1)',
      posterGradient: 'linear-gradient(to top, rgba(10, 10, 11, 1) 0%, rgba(10, 10, 11, 0.4) 40%, rgba(10, 10, 11, 0) 100%)'
    }
  },
  {
    key: 'dark-brown',
    label: 'Dark Brown',
    colors: {
      bg: '#1b1410',
      bgElevated: '#211814',
      headerBg: 'rgba(27, 20, 16, 0.88)',
      panel: 'rgba(255, 255, 255, 0.06)',
      panelStrong: 'rgba(255, 255, 255, 0.1)',
      panelSoft: 'rgba(255, 255, 255, 0.035)',
      border: 'rgba(255, 255, 255, 0.1)',
      borderStrong: 'rgba(255, 255, 255, 0.18)',
      text: '#f5eee6',
      textMuted: 'rgba(245, 238, 230, 0.65)',
      textSubtle: 'rgba(245, 238, 230, 0.48)',
      textFaint: 'rgba(245, 238, 230, 0.32)',
      textGhost: 'rgba(245, 238, 230, 0.2)',
      accent: '#facc15',
      accentHover: '#fde047',
      accentSoft: 'rgba(250, 204, 21, 0.18)',
      accentRing: 'rgba(250, 204, 21, 0.35)',
      accentShadow: 'rgba(250, 204, 21, 0.35)',
      accentContrast: '#1b1410',
      glassBg: 'rgba(36, 26, 20, 0.6)',
      glassBorder: 'rgba(255, 255, 255, 0.12)',
      posterGradient: 'linear-gradient(to top, rgba(27, 20, 16, 0.95) 0%, rgba(27, 20, 16, 0.45) 40%, rgba(27, 20, 16, 0) 100%)'
    }
  },
  {
    key: 'tan-brown',
    label: 'Tan Brown',
    colors: {
      bg: '#f3e5d6',
      bgElevated: '#f9efe4',
      headerBg: 'rgba(243, 229, 214, 0.9)',
      panel: 'rgba(59, 42, 31, 0.06)',
      panelStrong: 'rgba(59, 42, 31, 0.1)',
      panelSoft: 'rgba(59, 42, 31, 0.04)',
      border: 'rgba(59, 42, 31, 0.12)',
      borderStrong: 'rgba(59, 42, 31, 0.18)',
      text: '#3b2a1f',
      textMuted: 'rgba(59, 42, 31, 0.65)',
      textSubtle: 'rgba(59, 42, 31, 0.5)',
      textFaint: 'rgba(59, 42, 31, 0.35)',
      textGhost: 'rgba(59, 42, 31, 0.2)',
      accent: '#ffffff',
      accentHover: '#f8fafc',
      accentSoft: 'rgba(255, 255, 255, 0.45)',
      accentRing: 'rgba(255, 255, 255, 0.6)',
      accentShadow: 'rgba(255, 255, 255, 0.5)',
      accentContrast: '#3b2a1f',
      glassBg: 'rgba(255, 255, 255, 0.55)',
      glassBorder: 'rgba(59, 42, 31, 0.12)',
      posterGradient: 'linear-gradient(to top, rgba(59, 42, 31, 0.55) 0%, rgba(59, 42, 31, 0.25) 40%, rgba(59, 42, 31, 0) 100%)'
    }
  },
  {
    key: 'cyber-green',
    label: 'Cyberpunk light green',
    colors: {
      bg: '#0b1210',
      bgElevated: '#111b17',
      headerBg: 'rgba(11, 18, 16, 0.88)',
      panel: 'rgba(205, 255, 220, 0.06)',
      panelStrong: 'rgba(205, 255, 220, 0.12)',
      panelSoft: 'rgba(205, 255, 220, 0.035)',
      border: 'rgba(205, 255, 220, 0.14)',
      borderStrong: 'rgba(205, 255, 220, 0.22)',
      text: '#d7ffe2',
      textMuted: 'rgba(215, 255, 226, 0.65)',
      textSubtle: 'rgba(215, 255, 226, 0.48)',
      textFaint: 'rgba(215, 255, 226, 0.32)',
      textGhost: 'rgba(215, 255, 226, 0.2)',
      accent: '#ef4444',
      accentHover: '#f87171',
      accentSoft: 'rgba(239, 68, 68, 0.18)',
      accentRing: 'rgba(239, 68, 68, 0.35)',
      accentShadow: 'rgba(239, 68, 68, 0.35)',
      accentContrast: '#0b0b0b',
      glassBg: 'rgba(12, 20, 16, 0.7)',
      glassBorder: 'rgba(205, 255, 220, 0.18)',
      posterGradient: 'linear-gradient(to top, rgba(11, 18, 16, 0.95) 0%, rgba(11, 18, 16, 0.45) 40%, rgba(11, 18, 16, 0) 100%)'
    }
  },
  {
    key: 'cyber-purple',
    label: 'Cyberpunk purple',
    colors: {
      bg: '#120a1f',
      bgElevated: '#1a0f2b',
      headerBg: 'rgba(18, 10, 31, 0.88)',
      panel: 'rgba(201, 168, 255, 0.08)',
      panelStrong: 'rgba(201, 168, 255, 0.14)',
      panelSoft: 'rgba(201, 168, 255, 0.04)',
      border: 'rgba(201, 168, 255, 0.16)',
      borderStrong: 'rgba(201, 168, 255, 0.24)',
      text: '#f3e9ff',
      textMuted: 'rgba(243, 233, 255, 0.65)',
      textSubtle: 'rgba(243, 233, 255, 0.48)',
      textFaint: 'rgba(243, 233, 255, 0.32)',
      textGhost: 'rgba(243, 233, 255, 0.2)',
      accent: '#8bd3ff',
      accentHover: '#a5ddff',
      accentSoft: 'rgba(139, 211, 255, 0.22)',
      accentRing: 'rgba(139, 211, 255, 0.4)',
      accentShadow: 'rgba(139, 211, 255, 0.4)',
      accentContrast: '#0b1020',
      glassBg: 'rgba(18, 10, 31, 0.7)',
      glassBorder: 'rgba(201, 168, 255, 0.2)',
      posterGradient: 'linear-gradient(to top, rgba(18, 10, 31, 0.95) 0%, rgba(18, 10, 31, 0.45) 40%, rgba(18, 10, 31, 0) 100%)'
    }
  }
];

const App: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMovie, setSelectedMovie] = useState<Movie | null>(null);
  const [movieDetails, setMovieDetails] = useState<{ summary: string; sources: string[] } | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [selectedDecade, setSelectedDecade] = useState('All');
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPack, setSelectedPack] = useState(PACKS[0]);
  const [selectedThemeKey, setSelectedThemeKey] = useState('dark-brown');
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [showRandomModal, setShowRandomModal] = useState(false);
  const [randomMovie, setRandomMovie] = useState<Movie | null>(null);
  const [loadedPosterCount, setLoadedPosterCount] = useState(25);
  const [isLoadingPosters, setIsLoadingPosters] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);

  const footerFrame = useMemo(
    () => HERO_FRAMES[Math.floor(Math.random() * HERO_FRAMES.length)],
    []
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % HERO_FRAMES.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  // Reset loaded count when pack changes
  useEffect(() => {
    setLoadedPosterCount(25);
  }, [selectedPack]);

  const activeTheme = useMemo(
    () => THEMES.find((theme) => theme.key === selectedThemeKey) ?? THEMES[0],
    [selectedThemeKey]
  );

  const themeStyles = useMemo(
    () =>
      ({
        '--bg': activeTheme.colors.bg,
        '--bg-elevated': activeTheme.colors.bgElevated,
        '--header-bg': activeTheme.colors.headerBg,
        '--panel': activeTheme.colors.panel,
        '--panel-strong': activeTheme.colors.panelStrong,
        '--panel-soft': activeTheme.colors.panelSoft,
        '--border': activeTheme.colors.border,
        '--border-strong': activeTheme.colors.borderStrong,
        '--text': activeTheme.colors.text,
        '--text-muted': activeTheme.colors.textMuted,
        '--text-subtle': activeTheme.colors.textSubtle,
        '--text-faint': activeTheme.colors.textFaint,
        '--text-ghost': activeTheme.colors.textGhost,
        '--accent': activeTheme.colors.accent,
        '--accent-hover': activeTheme.colors.accentHover,
        '--accent-soft': activeTheme.colors.accentSoft,
        '--accent-ring': activeTheme.colors.accentRing,
        '--accent-shadow': activeTheme.colors.accentShadow,
        '--accent-contrast': activeTheme.colors.accentContrast,
        '--glass-bg': activeTheme.colors.glassBg,
        '--glass-border': activeTheme.colors.glassBorder,
        '--poster-gradient': activeTheme.colors.posterGradient
      }) as React.CSSProperties,
    [activeTheme]
  );

  useEffect(() => {
    document.body.style.backgroundColor = activeTheme.colors.bg;
    document.body.style.color = activeTheme.colors.text;
  }, [activeTheme]);

  const buildPosterUrl = useCallback((movie: Movie) => {
    if (movie.posterUrl) {
      return `/api/image?url=${encodeURIComponent(movie.posterUrl)}`;
    }
    if ((movie.pack === "Shin's" || movie.pack === 'P2P' || movie.pack === 'Exploitation' || movie.pack === 'Mexicano' || movie.pack === 'Pinkueiga' || movie.pack === 'Pacho' || movie.pack === 'Nomad' || movie.pack === 'Terror' || movie.pack === 'Derniers') && movie.letterboxd) {
      // Add cache busster for packs that scrape posters
      const cacheBuster = movie.pack === 'Pinkueiga' ? `&_=${movie.id}` : '';
      return `/api/image?url=${encodeURIComponent(movie.letterboxd)}${cacheBuster}`;
    }
    return '';
  }, []);

  const moviesWithPack = useMemo(
    () => MOVIE_DATA.map((movie) => ({ ...movie, pack: movie.pack ?? PACKS[0] })),
    []
  );

  const packMovies = useMemo(
    () => moviesWithPack.filter((movie) => movie.pack === selectedPack),
    [moviesWithPack, selectedPack]
  );

  const filteredMovies = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return packMovies.filter(movie => {
      // Text search
      const matchesSearch = !query ||
        movie.title.toLowerCase().includes(query) ||
        movie.originalTitle.toLowerCase().includes(query) ||
        movie.director.toLowerCase().includes(query) ||
        movie.year.toString().includes(query);

      // Decade filter
      let matchesDecade = true;
      if (selectedDecade !== 'All') {
        const decadeStart = parseInt(selectedDecade);
        matchesDecade = movie.year >= decadeStart && movie.year < decadeStart + 10;
      }

      // Genre filter (movie must match ALL selected genres)
      const matchesGenre = selectedGenres.length === 0 ||
        selectedGenres.every(genre => movie.genre.includes(genre));

      return matchesSearch && matchesDecade && matchesGenre;
    });
  }, [packMovies, searchQuery, selectedDecade, selectedGenres]);

  const moviesWithPosters = useMemo(
    () => {
      // For Shins Pack, load all posters at once (they have cached posters)
      if (selectedPack === "Shin's Pack") {
        return filteredMovies.map((movie) => {
          const posterUrl = buildPosterUrl(movie);
          return posterUrl ? { ...movie, posterUrl } : movie;
        });
      }

      // For P2P Google Pack, Exploitation Pack, Jotta Pack, and Pinkueiga Pack - load all posters
      return filteredMovies.map((movie) => {
        const posterUrl = buildPosterUrl(movie);
        return posterUrl ? { ...movie, posterUrl } : movie;
      });
    },
    [filteredMovies, buildPosterUrl, selectedPack]
  );

  const hasMorePosters = useMemo(
    () => loadedPosterCount < filteredMovies.length,
    [loadedPosterCount, filteredMovies.length]
  );

  const loadMorePosters = useCallback(() => {
    if (!isLoadingPosters && hasMorePosters) {
      setIsLoadingPosters(true);
      // Load 25 more posters
      setLoadedPosterCount(prev => Math.min(prev + 25, filteredMovies.length));
      setTimeout(() => setIsLoadingPosters(false), 100);
    }
  }, [isLoadingPosters, hasMorePosters, filteredMovies.length]);
  const randomPosterUrl = useMemo(() => (randomMovie ? buildPosterUrl(randomMovie) : ''), [randomMovie, buildPosterUrl]);

  const activeFilterCount = (selectedDecade !== 'All' ? 1 : 0) + selectedGenres.length;

  const toggleGenre = useCallback((genre: string) => {
    setSelectedGenres(prev =>
      prev.includes(genre)
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedDecade('All');
    setSelectedGenres([]);
  }, []);

  const handleThemeSelect = useCallback((key: string) => {
    setSelectedThemeKey(key);
    setShowThemeMenu(false);
  }, []);

  const handleMovieClick = useCallback(async (movie: Movie) => {
    setSelectedMovie(movie);
    setMovieDetails(null);
    setIsLoadingDetails(true);
    const details = await geminiService.getMovieDetails(movie.title, movie.year);
    if (details) {
      setMovieDetails(details);
    }
    setIsLoadingDetails(false);
  }, []);

  const getRandomMovie = useCallback(() => {
    const source = filteredMovies.length > 0 ? filteredMovies : packMovies;
    if (source.length === 0) {
      return null;
    }
    return source[Math.floor(Math.random() * source.length)];
  }, [filteredMovies, packMovies]);

  const handleRandomWatch = useCallback(() => {
    const nextMovie = getRandomMovie();
    setRandomMovie(nextMovie);
    setShowRandomModal(true);
  }, [getRandomMovie]);

  const handleShuffleRandom = useCallback(() => {
    const nextMovie = getRandomMovie();
    if (nextMovie) {
      setRandomMovie(nextMovie);
    }
  }, [getRandomMovie]);


  return (
    <div
      style={themeStyles}
      className="min-h-screen pb-24 bg-[color:var(--bg)] text-[color:var(--text)] selection:bg-[color:var(--accent)] selection:text-[color:var(--accent-contrast)]"
    >
      <header className="sticky top-0 z-[60] bg-[color:var(--header-bg)] backdrop-blur-xl border-b border-[color:var(--border)] px-6 py-4">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-6">
          <div className="flex items-center gap-3 md:justify-start">
            <div className="relative">
              <button
                onClick={() => setShowThemeMenu((prev) => !prev)}
                className={`relative flex-shrink-0 w-[52px] h-[52px] rounded-full border flex items-center justify-center transition-all duration-300 ${showThemeMenu
                  ? 'bg-[color:var(--accent)] border-[color:var(--accent)] text-[color:var(--accent-contrast)] shadow-lg shadow-[0_10px_30px_var(--accent-shadow)]'
                  : 'bg-[color:var(--panel)] border-[color:var(--border)] text-[color:var(--text-faint)] hover:text-[color:var(--text)] hover:bg-[color:var(--panel-strong)]'
                  }`}
              >
                <i className="fa-solid fa-user text-lg"></i>
              </button>
              {showThemeMenu && (
                <div className="absolute left-0 mt-3 w-64 rounded-2xl border border-[color:var(--border)] bg-[color:var(--bg-elevated)] shadow-2xl p-3 z-[70]">
                  <div className="flex items-center gap-3 px-4 py-3 mb-2 rounded-xl bg-[color:var(--panel-soft)] border border-[color:var(--border)]">
                    <div className="w-10 h-10 rounded-full bg-[color:var(--accent)] flex items-center justify-center text-[color:var(--accent-contrast)]">
                      <i className="fa-solid fa-user text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-black leading-none">Guest Viewer</p>
                      <p className="text-[10px] text-[color:var(--text-faint)] font-bold mt-1">Seven Seas Theatre</p>
                    </div>
                  </div>
                  <p className="text-[9px] font-black uppercase tracking-[0.4em] text-[color:var(--text-faint)] px-4 pt-2 pb-1">
                    <i className="fa-solid fa-palette mr-1.5 text-[color:var(--accent)]"></i>Appearance
                  </p>
                  {THEMES.map((theme) => (
                    <button
                      key={theme.key}
                      onClick={() => handleThemeSelect(theme.key)}
                      className={`w-full text-left px-4 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all flex items-center justify-between ${theme.key === selectedThemeKey
                        ? 'bg-[color:var(--accent)] text-[color:var(--accent-contrast)]'
                        : 'text-[color:var(--text-muted)] hover:bg-[color:var(--panel)] hover:text-[color:var(--text)]'
                        }`}
                    >
                      <span>{theme.label}</span>
                      {theme.key === selectedThemeKey && <i className="fa-solid fa-check text-xs"></i>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-center">
            <div>
              <h1 className="text-2xl font-black tracking-tight leading-none uppercase">The Seven Seas <span className="text-[color:var(--accent)]">Theatre</span></h1>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto md:justify-end">
            <div className="relative flex items-center justify-end">
              {searchExpanded ? (
                <div className="relative flex-1 md:w-[400px] animate-in fade-in slide-in-from-right-4 duration-300">
                  <i className="fa-solid fa-search absolute left-5 top-1/2 -translate-y-1/2 text-[color:var(--text-faint)]"></i>
                  <input
                    type="text"
                    placeholder="Search by film, creator, or era..."
                    autoFocus
                    className="w-full bg-[color:var(--panel)] border border-[color:var(--border)] rounded-2xl py-3.5 pl-14 pr-14 text-sm text-[color:var(--text)] placeholder:text-[color:var(--text-faint)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-ring)] transition-all focus:bg-[color:var(--panel-strong)]"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <button
                    onClick={() => setSearchExpanded(false)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-[color:var(--panel-strong)] hover:bg-[color:var(--accent)] hover:text-[color:var(--accent-contrast)] flex items-center justify-center text-[color:var(--text-faint)] transition-all"
                  >
                    <i className="fa-solid fa-xmark text-sm"></i>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setSearchExpanded(true)}
                  className="flex-shrink-0 w-[52px] h-[52px] rounded-2xl border border-[color:var(--border)] bg-[color:var(--panel)] text-[color:var(--text-faint)] hover:text-[color:var(--text)] hover:bg-[color:var(--panel-strong)] flex items-center justify-center transition-all duration-300"
                >
                  <i className="fa-solid fa-search text-lg"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative max-w-[1400px] mx-auto px-6 pt-16 pb-12 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[640px] pointer-events-none">
          {HERO_FRAMES.map((frame, i) => (
            <div
              key={frame}
              className="absolute inset-0 transition-opacity duration-[2500ms] ease-in-out"
              style={{ opacity: i === heroIndex ? 1 : 0 }}
            >
              <img src={frame} alt="" className="w-full h-full object-cover" loading={i < 3 ? 'eager' : 'lazy'} />
            </div>
          ))}
          <div className="absolute inset-0" style={{ backgroundColor: 'var(--bg)', opacity: 0.4 }}></div>
          <div className="absolute inset-x-0 top-0 h-40" style={{ background: 'linear-gradient(to bottom, var(--bg), transparent)' }}></div>
          <div className="absolute inset-x-0 bottom-0 h-56" style={{ background: 'linear-gradient(to top, var(--bg), transparent)' }}></div>
        </div>

        <div className="relative">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 border-b border-[color:var(--border)] pb-12">
          <div className="max-w-3xl">
            <h2 className="text-6xl md:text-8xl font-black mb-6 tracking-tighter uppercase leading-[0.9]">Explore the<br /><span className="text-transparent border-t border-b border-[color:var(--border)] py-2 inline-block" style={{ WebkitTextStroke: '1px var(--text-faint)' }}>Entire P2P Library</span></h2>
            <p className="text-[color:var(--text-muted)] text-xl leading-relaxed font-light">
              An exhaustive digital index of {MOVIE_DATA.length} cinematic masterpieces across every shelf. Every entry is backed by secure cloud storage, ondemand and ready to view.
            </p>
          </div>
          <div className="flex gap-4">
            <div className="bg-[color:var(--panel)] border border-[color:var(--border)] px-6 py-4 rounded-3xl text-center min-w-[120px]">
              <div className="text-3xl font-black text-[color:var(--accent)]">{packMovies.length}</div>
              <div className="text-[9px] uppercase tracking-widest text-[color:var(--text-subtle)] font-bold">Entries</div>
            </div>
            <div className="bg-[color:var(--panel)] border border-[color:var(--border)] px-6 py-4 rounded-3xl text-center min-w-[120px]">
              <div className="text-3xl font-black text-[color:var(--accent)]">{MOVIE_DATA.length}</div>
              <div className="text-[9px] uppercase tracking-widest text-[color:var(--text-subtle)] font-bold">Total Titles</div>
            </div>
          </div>
        </div>

        {/* View Switcher & Toolbar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex bg-[color:var(--panel)] border border-[color:var(--border)] rounded-2xl p-1 shadow-2xl">
            {PACKS.map((pack) => (
              <button
                key={pack}
                onClick={() => setSelectedPack(pack)}
                className={`px-5 py-2.5 rounded-xl text-[10px] font-black tracking-[0.2em] uppercase transition-all duration-300 ${selectedPack === pack ? 'bg-[color:var(--accent)] text-[color:var(--accent-contrast)] shadow-lg shadow-[0_10px_24px_var(--accent-shadow)]' : 'text-[color:var(--text-faint)] hover:text-[color:var(--text)] hover:bg-[color:var(--panel-strong)]'}`}
              >
                {pack}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`relative px-5 py-2.5 rounded-xl text-[10px] font-black tracking-[0.2em] uppercase transition-all duration-300 border border-[color:var(--border)] flex items-center gap-2 ${showFilters || activeFilterCount > 0
                ? 'bg-[color:var(--accent)] text-[color:var(--accent-contrast)] border-[color:var(--accent)] shadow-lg shadow-[0_10px_24px_var(--accent-shadow)]'
                : 'bg-[color:var(--panel)] text-[color:var(--text-faint)] hover:text-[color:var(--text)] hover:bg-[color:var(--panel-strong)]'
                }`}
            >
              <i className="fa-solid fa-sliders"></i>
              {activeFilterCount > 0 && (
                <span className="w-5 h-5 bg-[color:var(--accent)] text-[color:var(--accent-contrast)] rounded-full text-[10px] font-black flex items-center justify-center shadow-lg">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <button
              onClick={handleRandomWatch}
              className="px-5 py-2.5 rounded-xl text-[10px] font-black tracking-[0.2em] uppercase transition-all duration-300 bg-[color:var(--panel)] text-[color:var(--text-faint)] hover:text-[color:var(--text)] hover:bg-[color:var(--panel-strong)] border border-[color:var(--border)] flex items-center gap-2"
            >
              <i className="fa-solid fa-play"></i> <i className="fa-solid fa-shuffle"></i>
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        <div className={`overflow-hidden transition-all duration-500 ease-out ${showFilters ? 'max-h-[500px] opacity-100 mt-6' : 'max-h-0 opacity-0 mt-0'}`}>
          <div className="bg-[color:var(--panel-soft)] border border-[color:var(--border)] rounded-3xl p-6 space-y-6">
            {/* Decade Filter */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-[10px] font-black text-[color:var(--text-subtle)] uppercase tracking-[0.4em]">
                  <i className="fa-solid fa-calendar mr-2 text-[color:var(--accent)] opacity-60"></i>Era
                </h4>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="text-[10px] font-black text-[color:var(--accent)] uppercase tracking-widest hover:text-[color:var(--accent-hover)] transition-colors flex items-center gap-1.5"
                  >
                    <i className="fa-solid fa-xmark"></i> Clear All
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {DECADES.map(decade => (
                  <button
                    key={decade}
                    onClick={() => setSelectedDecade(decade)}
                    className={`px-4 py-2 rounded-xl text-[11px] font-black uppercase tracking-wider transition-all duration-300 ${selectedDecade === decade
                      ? 'bg-[color:var(--accent)] text-[color:var(--accent-contrast)] shadow-lg shadow-[0_10px_24px_var(--accent-shadow)]'
                      : 'bg-[color:var(--panel)] text-[color:var(--text-faint)] hover:text-[color:var(--text)] hover:bg-[color:var(--panel-strong)] border border-[color:var(--border)]'
                      }`}
                  >
                    {decade}
                  </button>
                ))}
              </div>
            </div>

            {/* Genre Filter */}
            <div>
              <h4 className="text-[10px] font-black text-[color:var(--text-subtle)] uppercase tracking-[0.4em] mb-3">
                <i className="fa-solid fa-masks-theater mr-2 text-[color:var(--accent)] opacity-60"></i>Genre
              </h4>
              <div className="flex flex-wrap gap-2">
                {ALL_GENRES.map(genre => (
                  <button
                    key={genre}
                    onClick={() => toggleGenre(genre)}
                    className={`px-4 py-2 rounded-xl text-[11px] font-bold tracking-wider transition-all duration-300 ${selectedGenres.includes(genre)
                      ? 'bg-[color:var(--accent)] text-[color:var(--accent-contrast)] shadow-lg shadow-[0_10px_24px_var(--accent-shadow)]'
                      : 'bg-[color:var(--panel)] text-[color:var(--text-faint)] hover:text-[color:var(--text)] hover:bg-[color:var(--panel-strong)] border border-[color:var(--border)]'
                      }`}
                  >
                    {genre}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Movie Content */}
        {moviesWithPosters.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-10">
                {moviesWithPosters.map(movie => (
                  <MovieCard
                    key={movie.id}
                    movie={movie}
                    onClick={handleMovieClick}
                  />
                ))}
              </div>

            {/* Load More Button for Lazy Loading Packs */}
            {hasMorePosters && selectedPack !== "Shin's Pack" && (
              <div className="flex justify-center mt-12">
                <button
                  onClick={loadMorePosters}
                  disabled={isLoadingPosters}
                  className="px-8 py-4 bg-[color:var(--panel)] border border-[color:var(--border)] rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-[color:var(--accent)] hover:text-[color:var(--accent-contrast)] hover:border-[color:var(--accent)] transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  {isLoadingPosters ? (
                    <><i className="fa-solid fa-spinner fa-spin"></i> Loading...</>
                  ) : (
                    <><i className="fa-solid fa-plus"></i> Load More ({filteredMovies.length - loadedPosterCount} remaining)</>
                  )}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-40 border-2 border-dashed border-[color:var(--border)] rounded-[40px]">
            <i className="fa-solid fa-ghost text-6xl text-[color:var(--text-ghost)] mb-8"></i>
            <h3 className="text-2xl font-black uppercase tracking-widest">No Matches Found</h3>
            <p className="text-[color:var(--text-subtle)] mt-3 font-medium">The vault does not contain records for this query.</p>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="mt-8 px-8 py-3 bg-[color:var(--accent)] text-[color:var(--accent-contrast)] rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-[color:var(--accent-hover)] transition-all shadow-lg shadow-[0_10px_24px_var(--accent-shadow)]"
              >
                <i className="fa-solid fa-filter-circle-xmark mr-2"></i>Clear Filters
              </button>
            )}
          </div>
        )}
        </div>
      </section>

      {showRandomModal && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 sm:p-8">
          <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={() => setShowRandomModal(false)}></div>
          <div className="relative bg-[color:var(--bg-elevated)] border border-[color:var(--border)] w-full max-w-4xl rounded-[36px] shadow-2xl overflow-hidden">
            <button className="absolute top-6 right-6 z-20 w-11 h-11 bg-[color:var(--panel)] hover:bg-[color:var(--accent)] hover:rotate-90 text-[color:var(--text)] hover:text-[color:var(--accent-contrast)] rounded-2xl flex items-center justify-center transition-all duration-500" onClick={() => setShowRandomModal(false)}>
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>
            <div className="p-8 sm:p-12">
              <div className="flex items-center justify-between gap-6 mb-8">
                <div>
                  <p className="text-[10px] text-[color:var(--text-faint)] uppercase tracking-[0.4em] font-bold">Shuffle Choice</p>
                  <h3 className="text-3xl sm:text-4xl font-black uppercase tracking-tight">Random Watch</h3>
                </div>
                <button
                  onClick={handleShuffleRandom}
                  className="px-6 py-3 rounded-2xl text-[10px] font-black tracking-[0.3em] uppercase transition-all duration-300 bg-[color:var(--panel)] text-[color:var(--text-faint)] hover:text-[color:var(--text)] hover:bg-[color:var(--panel-strong)] border border-[color:var(--border)] flex items-center gap-2"
                >
                  Shuffle Again <i className="fa-solid fa-shuffle"></i>
                </button>
              </div>
              {randomMovie ? (
                <div key={randomMovie.id} className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8 items-center">
                  <div className="relative aspect-[2/3] overflow-hidden rounded-3xl bg-black/40 border border-[color:var(--border)] shadow-2xl shuffle-rise shuffle-rise-delay-1">
                    {randomPosterUrl ? (
                      <img src={randomPosterUrl} alt={`${randomMovie.title} poster`} className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-center gap-3 text-[color:var(--text-ghost)]">
                        <i className="fa-solid fa-film text-3xl"></i>
                        <span className="text-[10px] font-black tracking-[0.3em] uppercase">Poster Missing</span>
                      </div>
                    )}
                    <div className="absolute inset-0 poster-gradient opacity-90 pointer-events-none"></div>
                  </div>
                  <div className="shuffle-rise shuffle-rise-delay-2">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="bg-[color:var(--accent)] text-[color:var(--accent-contrast)] px-4 py-1.5 rounded-full font-black tracking-widest text-[11px] uppercase shadow-lg shadow-[0_10px_24px_var(--accent-shadow)]">{randomMovie.year}</span>
                      <span className="text-[color:var(--text-faint)] uppercase tracking-[0.3em] text-[10px] font-bold">Archive Record {randomMovie.id.padStart(3, '0')}</span>
                    </div>
                    <h2 className="text-4xl sm:text-5xl font-black leading-[0.95] tracking-tighter uppercase">{randomMovie.title}</h2>
                    <p className="text-[color:var(--accent)] opacity-70 font-serif italic text-xl mt-3">{randomMovie.originalTitle}</p>
                    <div className="mt-6 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[color:var(--panel)] flex items-center justify-center text-[color:var(--accent)]">
                        <i className="fa-solid fa-clapperboard"></i>
                      </div>
                      <div>
                        <p className="text-[9px] uppercase tracking-widest text-[color:var(--text-subtle)] font-bold">Director</p>
                        <p className="text-lg font-bold">{randomMovie.director}</p>
                      </div>
                    </div>
                    <div className="mt-10">
                      <button
                        className="w-full bg-[color:var(--accent)] text-[color:var(--accent-contrast)] py-4 rounded-2xl text-xs font-black tracking-widest hover:bg-[color:var(--accent-hover)] transition-all flex items-center justify-center gap-3 shadow-2xl"
                        onClick={() => window.open(randomMovie.drive, '_blank')}
                      >
                        WATCH NOW <i className="fa-solid fa-play text-sm"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-16 text-center">
                  <p className="text-[color:var(--text-subtle)] text-lg font-medium">No movies are available for the current selection.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Movie Detail Overlay */}
      {selectedMovie && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-10">
          <div className="absolute inset-0 bg-black/95 backdrop-blur-md" onClick={() => setSelectedMovie(null)}></div>
          <div className="relative bg-[color:var(--bg-elevated)] border border-[color:var(--border)] w-full max-w-6xl max-h-[95vh] overflow-y-auto rounded-[40px] shadow-2xl flex flex-col lg:flex-row overflow-hidden">
            <button className="absolute top-8 right-8 z-20 w-12 h-12 bg-[color:var(--panel)] hover:bg-[color:var(--accent)] hover:rotate-90 text-[color:var(--text)] hover:text-[color:var(--accent-contrast)] rounded-2xl flex items-center justify-center transition-all duration-500" onClick={() => setSelectedMovie(null)}>
              <i className="fa-solid fa-xmark text-xl"></i>
            </button>
            <div className="flex-1 p-8 sm:p-20 flex flex-col justify-center">
              <div className="mb-12">
                <div className="flex items-center gap-4 mb-6">
                  <span className="bg-[color:var(--accent)] text-[color:var(--accent-contrast)] px-4 py-1.5 rounded-full font-black tracking-widest text-[11px] uppercase shadow-lg shadow-[0_10px_24px_var(--accent-shadow)]">{selectedMovie.year}</span>
                  <span className="text-[color:var(--text-faint)] uppercase tracking-[0.3em] text-[10px] font-bold">Archive Record {selectedMovie.id.padStart(3, '0')}</span>
                </div>
                <h2 className="text-5xl md:text-7xl font-black leading-[0.9] tracking-tighter uppercase">{selectedMovie.title}</h2>
                <p className="text-[color:var(--accent)] opacity-70 font-serif italic text-2xl mt-4">{selectedMovie.originalTitle}</p>
                <div className="mt-8 flex flex-wrap gap-10 items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[color:var(--panel)] flex items-center justify-center text-[color:var(--accent)]">
                      <i className="fa-solid fa-clapperboard"></i>
                    </div>
                    <div>
                      <p className="text-[9px] uppercase tracking-widest text-[color:var(--text-subtle)] font-bold">Director</p>
                      <p className="text-lg font-bold">{selectedMovie.director}</p>
                    </div>
                  </div>
                </div>
                {/* Genre Tags in Detail View */}
                <div className="mt-6 flex flex-wrap gap-2">
                  {selectedMovie.genre.map(g => (
                    <span key={g} className="px-3 py-1 bg-[color:var(--panel)] border border-[color:var(--border)] rounded-full text-[10px] font-bold text-[color:var(--text-muted)] uppercase tracking-wider">
                      {g}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex-1 max-w-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-[1px] flex-1 bg-[color:var(--border)]"></div>
                  <h4 className="text-[10px] font-black text-[color:var(--text-subtle)] uppercase tracking-[0.4em]">Curator Analysis</h4>
                  <div className="h-[1px] flex-1 bg-[color:var(--border)]"></div>
                </div>
                {isLoadingDetails ? (
                  <div className="space-y-6">
                    <div className="h-4 bg-[color:var(--panel)] animate-pulse rounded-full w-full"></div>
                    <div className="h-4 bg-[color:var(--panel)] animate-pulse rounded-full w-[95%]"></div>
                    <div className="h-4 bg-[color:var(--panel)] animate-pulse rounded-full w-[80%]"></div>
                  </div>
                ) : movieDetails ? (
                  <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <p className="text-[color:var(--text-muted)] leading-relaxed text-xl font-light">{movieDetails.summary}</p>
                    {movieDetails.sources.length > 0 && (
                      <div className="mt-10">
                        <p className="text-[10px] text-[color:var(--text-faint)] uppercase tracking-[0.3em] mb-4 font-bold">Neural Grounding Citations</p>
                        <div className="flex flex-wrap gap-4">
                          {movieDetails.sources.map((src, idx) => (
                            <a key={idx} href={src} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-[color:var(--panel)] border border-[color:var(--border)] rounded-2xl text-[11px] font-bold text-[color:var(--text)] hover:bg-[color:var(--accent-soft)] hover:border-[color:var(--accent)] transition-all flex items-center gap-2 group/link">
                              <i className="fa-solid fa-fingerprint text-[color:var(--accent)] group-hover:animate-pulse"></i>
                              {new URL(src).hostname.replace('www.', '')}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : <p className="text-[color:var(--text-subtle)] text-lg italic font-light">Decrypting archive notes...</p>}
              </div>
              <div className="mt-16 flex flex-col sm:flex-row gap-6">
                <a href={selectedMovie.drive} target="_blank" rel="noopener noreferrer" className="flex-1 bg-[color:var(--accent)] text-[color:var(--accent-contrast)] hover:bg-[color:var(--accent-hover)] font-black py-6 rounded-[24px] flex items-center justify-center gap-4 shadow-2xl transition-all active:scale-95 text-lg">
                  <i className="fa-brands fa-google-drive text-2xl"></i> OPEN THE VAULT
                </a>
                <a href={selectedMovie.letterboxd} target="_blank" rel="noopener noreferrer" className="px-10 py-6 bg-[color:var(--panel)] border border-[color:var(--border)] hover:bg-[color:var(--accent)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent-contrast)] rounded-[24px] flex items-center justify-center gap-3 transition-all active:scale-95">
                  <i className="fa-solid fa-database text-[color:var(--text-faint)]"></i> METADATA
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <footer className="relative mt-32 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <img src={footerFrame} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ backgroundColor: 'var(--bg)', opacity: 0.82 }}></div>
          <div className="absolute inset-x-0 top-0 h-32" style={{ background: 'linear-gradient(to bottom, var(--bg), transparent)' }}></div>
          <div className="absolute inset-x-0 bottom-0 h-32" style={{ background: 'linear-gradient(to top, var(--bg), transparent)' }}></div>
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 pt-24 pb-16 min-h-[520px] flex flex-col justify-between">
          <div className="text-center">
            <p className="text-[10px] text-[color:var(--text-faint)] uppercase tracking-[0.8em] font-black mb-6">A Permanent Feature Presentation</p>
            <h2 className="text-5xl md:text-8xl font-black tracking-tighter uppercase leading-[0.85]">The Seven Seas<br /><span className="text-transparent" style={{ WebkitTextStroke: '1.5px var(--accent)' }}>Theatre Unit</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-end mt-20">
            <div>
              <h5 className="text-[10px] font-black uppercase tracking-[0.5em] text-[color:var(--text-faint)] mb-3">The Vault</h5>
              <p className="text-[color:var(--text-subtle)] text-sm leading-relaxed max-w-md font-medium">
                {MOVIE_DATA.length} curated titles rendered as a high-fidelity visual index. Every film is delivered through secure cloud storage, ondemand and ready to view.
              </p>
            </div>
            <div className="flex justify-center md:justify-center">
              <div className="w-20 h-20 bg-[color:var(--accent)] rounded-3xl flex items-center justify-center shadow-[0_0_40px_var(--accent-shadow)] rotate-3 hover:rotate-0 transition-all duration-700">
                <i className="fa-solid fa-film text-3xl text-[color:var(--accent-contrast)]"></i>
              </div>
            </div>
            <div className="text-center md:text-right">
              <div className="flex items-center gap-3 justify-center md:justify-end bg-[color:var(--accent-soft)] border border-[color:var(--accent)] px-6 py-3 rounded-full">
                <div className="w-2 h-2 bg-[color:var(--accent)] rounded-full animate-pulse shadow-[0_0_10px_var(--accent-shadow)]"></div>
                <span className="text-[10px] font-black text-[color:var(--accent)] tracking-widest uppercase">Archive Operational</span>
              </div>
            </div>
          </div>
          <div className="mt-24 pt-8 border-t border-[color:var(--border)] flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[10px] text-[color:var(--text-ghost)] uppercase tracking-[0.6em] font-black">&copy; MMXXIV THE SEVEN SEAS THEATRE UNIT</p>
            <p className="text-[10px] text-[color:var(--text-ghost)] uppercase tracking-[0.3em] font-bold">Still frames via StillsLab</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
